import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Lottie from 'lottie-react'
import { faClose, faArrowRight } from '@fortawesome/pro-regular-svg-icons'
import { useReselector } from '@/utils/sharedHooks'

import { Button, Card, Icon, Text, CardColors } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'
import {
  selectFederalEstimateByTaxQuarter,
  selectStatusForTaxEstimate,
} from '../userTaxEstimates.selector'

import taxBreakdown from '@/src/assets/lottie/taxBreakdown.json'
import { createLinkToTaxBreakdown } from './row-components/TaxEstimateCta'
import {
  BreakdownVersion,
  selectCalculatorVersionByCalcId,
  selectUserTaxCalculationWithinActiveQuarter,
} from '@/selectors/userTaxCalculation.selectors'
import { EstimateStatus } from '../service'
import { selectActiveQTEDetailsAsDates } from '@/features/admin/quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.selector'

const BANNER_CLOSED_KEY = 'breakdown_banner_closed'

export const BreakdownBanner = () => {
  const navigate = useNavigate()
  const [bannerDismissed, setBannerDismissed] = useState(
    localStorage.getItem(BANNER_CLOSED_KEY) === 'true'
  )
  const activeQuarterDetails = useReselector(selectActiveQTEDetailsAsDates)

  const federalTaxEstimate = useReselector(
    selectFederalEstimateByTaxQuarter,
    `${activeQuarterDetails?.taxYear}-${activeQuarterDetails?.taxQuarter}`
  )
  const calculation = useReselector(selectUserTaxCalculationWithinActiveQuarter)

  const hasBreakdownPage =
    useReselector(selectCalculatorVersionByCalcId, calculation?.id) !==
    BreakdownVersion.defaultBreakdown

  const status = useReselector(
    selectStatusForTaxEstimate,
    federalTaxEstimate?.id
  )

  if (
    !bannerDismissed &&
    federalTaxEstimate &&
    hasBreakdownPage &&
    status &&
    ![
      EstimateStatus.awaitingEstimate,
      EstimateStatus.upcoming,
      EstimateStatus.joinedTooLate,
    ].includes(status)
  ) {
    return (
      <Card color={CardColors.LEVEL_1}>
        <div className="flex flex-wrap">
          <Button
            aria-label="Dismiss banner"
            className="absolute top-2 right-2"
            iconRight={faClose}
            variant="ghost"
            size="sm"
            onClick={() => {
              localStorage.setItem('breakdown_banner_closed', 'true')
              setBannerDismissed(true)
            }}
          />

          <Lottie
            animationData={taxBreakdown}
            loop
            className="rounded-xl mb-5 mr-6 h-[288px] w-[450px] shadow-lg"
          />

          <div className="max-w-[550px]">
            <Text variant={TypographyStyles.H1}>
              See how Heard calculated your federal tax estimate
            </Text>
            <Text color={TextColors.MOSS} className="my-6">
              Visit your new breakdown page to view the numbers that went into
              your federal tax estimate calculation.
            </Text>
            <Button
              onClick={() =>
                navigate(createLinkToTaxBreakdown(federalTaxEstimate))
              }
              variant="secondary"
            >
              Let&apos;s See{' '}
              <Icon icon={faArrowRight} className="ml-2 inline-block p-0" />
            </Button>
          </div>
        </div>
      </Card>
    )
  }

  return null
}
